import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUniversalAuth } from '../../contexts/UniversalAuthContext';
import api from '../../services/api';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import '../../styles/auth/SetPassword.css';

const ForceChangePassword = () => {
  const navigate = useNavigate();
  const { user, logout, fetchMe } = useUniversalAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Le mot de passe doit contenir au moins une majuscule';
    }
    if (!/[0-9]/.test(password)) {
      return 'Le mot de passe doit contenir au moins un chiffre';
    }
    if (!/[!@#$%^&*]/.test(password)) {
      return 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validations
    if (newPassword !== confirmPassword) {
      setError('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (currentPassword === newPassword) {
      setError('Le nouveau mot de passe doit être différent de l\'ancien');
      return;
    }

    setLoading(true);

    try {
      await api.put('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });

      // CRITIQUE: Rafraîchir l'utilisateur pour mettre à jour must_change_password
      await fetchMe();

      alert('✅ Mot de passe changé avec succès ! Vous allez être redirigé vers le dashboard.');
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  return (
    <div className="set-password-page">
      <div className="set-password-container">
        <div className="logo-section">
          <h1>🔐 NVF</h1>
          <p>Naturellement Vert Fertilisation</p>
        </div>

        <div className="alert-box">
          <AlertCircle size={24} />
          <div>
            <h3>Changement de mot de passe obligatoire</h3>
            <p>Pour des raisons de sécurité, vous devez changer votre mot de passe avant d'accéder au système.</p>
          </div>
        </div>

        <h2>Créez votre nouveau mot de passe</h2>
        <p className="set-password-subtitle">
          Bonjour {user?.first_name || user?.email}, bienvenue !
        </p>

        <form onSubmit={handleSubmit} className="set-password-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>Mot de passe temporaire actuel</label>
            <div className="password-input-wrapper">
              <Lock className="input-icon" size={20} />
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Entrez le mot de passe temporaire"
                required
                autoFocus
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowCurrent(!showCurrent)}
              >
                {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Nouveau mot de passe</label>
            <div className="password-input-wrapper">
              <Lock className="input-icon" size={20} />
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 caractères"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <small className="password-requirements">
              • Min 8 caractères • 1 majuscule • 1 chiffre • 1 caractère spécial
            </small>
          </div>

          <div className="form-group">
            <label>Confirmer le nouveau mot de passe</label>
            <div className="password-input-wrapper">
              <Lock className="input-icon" size={20} />
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmez votre mot de passe"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Changement en cours...' : 'Changer mon mot de passe'}
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="btn-secondary"
            disabled={loading}
          >
            Se déconnecter
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForceChangePassword;
